import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AddCircleContained, Loader03, Trash01 } from 'lib/lucky-x-icons-2';
import { useAppSelector } from 'store/hooks';
import { useUserInfo } from 'lib/user';
import trophyIcon from 'assets/emoji/trophy.png';
import { addressFormat } from 'lib/nicknameFormat';
import { useScroll, useSize, useToggle, useUpdateEffect } from 'ahooks';
import AddWhitelist from './AddWhitelist';
import DeleteWhitelist from './DeleteWhitelist';
import { ProjectWhitelist } from 'api/project/project_detail';
import classNames from 'classnames';
import { errMsg } from '@/components/PageElement/ErrorMessage';

interface IWhitelistProps {
  isChangeStyle?: boolean;
  id: number;
  getProjectWhitelistRaffle: (isUpdate?: boolean) => void;
  projectWhitelist: ProjectWhitelist[];
  requested: boolean;
}

const Whitelist: React.FC<IWhitelistProps> = ({
  isChangeStyle,
  id,
  getProjectWhitelistRaffle,
  projectWhitelist,
  requested,
}) => {
  const { isLogin } = useUserInfo();
  const [openAdd, { toggle: toggleAdd }] = useToggle();
  const [openDelete, { toggle: toggleDelete }] = useToggle();
  const [deleteId, setDeleteId] = useState(0);
  const [deleteAddr, setDeleteAddr] = useState('');
  const { screenWidth } = useAppSelector((state) => state.nav);
  const listRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef(null);
  const listScroll = useScroll(listRef);
  const [isScroll, setIsScroll] = useState(false);
  const contentSize = useSize(contentRef);

  useEffect(() => {
    if (listScroll?.top !== 0 && !!listScroll) {
      setIsScroll(true);
    }
  }, [listScroll, listScroll?.top]);

  useUpdateEffect(() => {
    if (isScroll) {
      setTimeout(() => {
        setIsScroll(false);
      }, 1000);
    }
  }, [listScroll?.top, listScroll?.top]);

  const addWhitelist = useCallback(() => {
    if (projectWhitelist?.length >= 200) {
      errMsg('The number of Whitelist address has reached its limit.');
    } else {
      toggleAdd();
    }
  }, [projectWhitelist?.length, toggleAdd]);

  const scrollToBottom = useMemo(() => {
    return Number(listScroll?.top) / (Number(contentSize?.height) - 150);
  }, [contentSize?.height, listScroll?.top]);

  const listRender = useCallback(() => {
    if (!isLogin) {
      return (
        <div
          className={classNames({
            'flex h-full items-center justify-center pr-5 text-center text-xs lg:text-sm':
              !isChangeStyle,
            'py-14 text-center text-xs': isChangeStyle,
          })}
        >
          Please connect wallet
        </div>
      );
    } else if (!requested) {
      return <Loader03 size={20} className="m-auto pt-5 text-neutral-100" />;
    } else if (!projectWhitelist || projectWhitelist?.length === 0) {
      return (
        <div
          className={classNames({
            'flex h-full items-center justify-center pr-5 text-center text-xs lg:text-sm':
              !isChangeStyle,
            'py-14 text-center text-xs': isChangeStyle,
          })}
        >
          No whitelist
        </div>
      );
    } else {
      return (
        <div
          className="relative h-[162px] overflow-y-scroll pt-3"
          ref={listRef}
        >
          <div
            className={classNames(
              'absolute right-1 hidden h-[110px] w-2 rounded-[49px] bg-neutral-700',
              {
                '!block': isScroll,
              }
            )}
            style={{
              top: Number(listScroll?.top) + 45 * scrollToBottom,
            }}
          ></div>
          <div className="grid grid-cols-1 gap-2 pr-5" ref={contentRef}>
            {projectWhitelist?.map((item) => (
              <div className="flex items-center gap-2" key={item?.address}>
                {item?.has_won && (
                  <img src={trophyIcon.src} className="w-5" alt="" />
                )}
                <div className="text-xs font-normal lg:text-sm">
                  {isChangeStyle || screenWidth < 768 ? (
                    <div className="w-[90px] text-xs">
                      {' '}
                      {addressFormat(item?.address)}
                    </div>
                  ) : (
                    <div className="w-[320px] lg:w-[360px] truncate text-xs lg:text-sm">
                      {' '}
                      {item?.address}
                    </div>
                  )}
                </div>
                {!item?.has_won && (
                  <div className="relative">
                    <Trash01
                      size={20}
                      className="peer cursor-pointer text-neutral-400 hover:text-neutral-100"
                      onClick={() => {
                        setDeleteId(item?.id);
                        setDeleteAddr(item?.address);
                        toggleDelete();
                      }}
                    />
                    <div
                      className={classNames(
                        'absolute   z-10 hidden w-[66px] rounded-lg border-2 border-neutral-700 bg-neutral-800 px-2 py-3 text-left text-sm font-normal  text-neutral-200 lg:peer-hover:block',
                        {
                          '-left-6 top-6': isChangeStyle,
                          '-right-[70px] -top-3': !isChangeStyle,
                        }
                      )}
                    >
                      Delete
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }
  }, [
    isChangeStyle,
    isLogin,
    isScroll,
    listScroll?.top,
    projectWhitelist,
    requested,
    screenWidth,
    scrollToBottom,
    toggleDelete,
  ]);

  return (
    <div className="h-full w-1/2 pl-5">
      <div className="relative  flex items-center gap-5">
        <div className="text-base font-semibold">Whitelist</div>
        {isLogin && (
          <AddCircleContained
            size={20}
            className="peer cursor-pointer text-neutral-400 hover:text-neutral-100"
            onClick={addWhitelist}
          />
        )}
        <div className="absolute -left-5 -top-20 z-20 hidden w-[236px] whitespace-pre-wrap  rounded-lg border-2 border-neutral-700 bg-neutral-800 px-2 py-3 text-left text-sm font-normal  text-neutral-200 lg:peer-hover:block">
          Add a WL address that you received from outside of LuckyGo.
        </div>
      </div>
      {listRender()}
      <AddWhitelist
        openAdd={openAdd}
        toggleAdd={toggleAdd}
        id={id}
        getProjectWhitelistRaffle={getProjectWhitelistRaffle}
      />
      <DeleteWhitelist
        openDelete={openDelete}
        toggleDelete={toggleDelete}
        id={id}
        deleteAddr={deleteAddr}
        getProjectWhitelistRaffle={getProjectWhitelistRaffle}
      />
    </div>
  );
};

export default Whitelist;
